import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { loginCustomer, logoutAllCustomerSessions, logoutCustomer, refreshCustomerSession, registerCustomer } from '../services/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialised = useRef(false);

  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    refreshCustomerSession()
      .then((data) => { setAccessToken(data.accessToken); setUser(data.user); })
      .catch(() => { setAccessToken(''); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (values) => {
    const data = await loginCustomer(values);
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback((values) => registerCustomer(values), []);

  const logout = useCallback(async () => {
    try { await logoutCustomer(); } finally { setAccessToken(''); setUser(null); }
  }, []);

  const logoutAll = useCallback(async () => {
    try { await logoutAllCustomerSessions(accessToken); } finally { setAccessToken(''); setUser(null); }
  }, [accessToken]);

  const value = useMemo(() => ({ accessToken, loading, login, logout, logoutAll, register, setUser, user }), [accessToken, loading, login, logout, logoutAll, register, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
